/**
 * TaskDetailModal Component
 * Displays full details of a task in a modal
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Calendar,
  Banknote,
  Tag,
  AlertCircle,
  CheckCircle2,
  Circle,
  Link as LinkIcon,
  List,
  Folder,
  FileText,
  Clock,
  Receipt,
  ShoppingCart,
  Target,
  ExternalLink
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { TASK_STATUS, TASK_PRIORITY } from '@/services/finTasksService';
import { getRelativeDueLabel, formatDate } from '@/utils/dates';
import { CONTEXT_TYPE, getContextPath, getContextColor, hasContextLink } from '@/utils/contextLinking';

const priorityColors = {
  low: 'text-gray-500',
  medium: 'text-blue-500',
  high: 'text-red-500'
};

const priorityBgColors = {
  low: 'bg-gray-100',
  medium: 'bg-blue-50',
  high: 'bg-red-50'
};

export default function TaskDetailModal({ task, isOpen, onClose }) {
  const navigate = useNavigate();
  const { currency: userCurrency } = useAuth();
  const currency = userCurrency || 'USD';
  
  if (!task) return null;

  const isDone = task.status === TASK_STATUS.DONE;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  if (dueDate) dueDate.setHours(0, 0, 0, 0);
  const isOverdue = dueDate && dueDate < today && !isDone;

  const relativeDueInfo = getRelativeDueLabel(task.dueDate, isDone);

  const getStatusInfo = () => {
    if (isDone) return { label: 'Completed', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle2 };
    if (isOverdue) return { label: 'Overdue', color: 'text-red-600', bg: 'bg-red-50', icon: AlertCircle };
    return { label: 'Pending', color: 'text-blue-600', bg: 'bg-blue-50', icon: Circle };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between rounded-t-2xl z-10">
                <div className="flex-1 pr-4">
                  <h2 className={cn(
                    "text-2xl font-bold text-gray-900",
                    isDone && "line-through text-gray-500"
                  )}>
                    {task.title}
                  </h2>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {/* Status Badge */}
                    <span className={cn(
                      'inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg',
                      statusInfo.color,
                      statusInfo.bg
                    )}>
                      <StatusIcon size={14} />
                      {statusInfo.label}
                    </span>
                    {/* Financial Badge */}
                    {task.isFinancialTask && (
                      <span className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700">
                        💰 Financial
                      </span>
                    )}
                    {/* Priority Badge */}
                    {task.priority && (
                      <span className={cn(
                        'text-sm font-semibold px-3 py-1.5 rounded-lg',
                        priorityColors[task.priority],
                        priorityBgColors[task.priority]
                      )}>
                        {task.priority.toUpperCase()} PRIORITY
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-6">
                {/* Description */}
                {task.description && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText size={18} className="text-gray-500" />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Description</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed pl-6">{task.description}</p>
                  </div>
                )}

                {/* Context Label - Linked To */}
                {task.contextLabel && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <LinkIcon size={18} className="text-purple-600" />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Linked To</h3>
                    </div>
                    <div className="pl-6">
                      {(() => {
                        const isClickable = hasContextLink(task);
                        const colors = getContextColor(task.contextType || 'none');
                        const ContextIcon = task.contextType === CONTEXT_TYPE.BUDGET ? Banknote :
                                           task.contextType === CONTEXT_TYPE.CATEGORY ? Folder :
                                           task.contextType === CONTEXT_TYPE.TRANSACTION ? Receipt :
                                           task.contextType === CONTEXT_TYPE.LIST ? ShoppingCart :
                                           task.contextType === CONTEXT_TYPE.GOAL ? Target :
                                           LinkIcon;

                        const handleClick = () => {
                          if (isClickable) {
                            const path = getContextPath(task.contextType, task.contextId);
                            if (path) {
                              navigate(path);
                              onClose();
                            }
                          }
                        };

                        return (
                          <button
                            onClick={handleClick}
                            disabled={!isClickable}
                            className={cn(
                              'inline-flex items-center gap-2 px-3 py-2 rounded-lg border font-medium transition-all',
                              colors.bg,
                              colors.text,
                              colors.border,
                              isClickable ? `${colors.hover} cursor-pointer` : 'cursor-default'
                            )}
                          >
                            <ContextIcon size={14} />
                            {task.contextLabel}
                            {isClickable && <ExternalLink size={12} className="ml-1" />}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Financial Amount */}
                {task.isFinancialTask && task.estimatedAmount && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Banknote size={18} className="text-[#EF476F]" />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Amount</h3>
                    </div>
                    <p className="text-3xl font-bold text-[#EF476F] pl-6">
                      {formatCurrency(task.estimatedAmount, currency)}
                    </p>
                  </div>
                )}

                {/* Due Date */}
                {task.dueDate && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={18} className={isOverdue ? "text-red-600" : "text-gray-500"} />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Due Date</h3>
                    </div>
                    <div className="pl-6 space-y-1">
                      <p className={cn(
                        "text-lg font-semibold",
                        isOverdue ? "text-red-600" : "text-gray-900"
                      )}>
                        {formatDate(task.dueDate)}
                      </p>
                      {relativeDueInfo && (
                        <p className={cn("text-sm font-medium", relativeDueInfo.color)}>
                          {relativeDueInfo.text}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Legacy Linked Context removed: use context linking section above */}

                {/* List, Category, Subcategory */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* List */}
                  {task.listName && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <List size={18} className="text-gray-500" />
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">List</h3>
                      </div>
                      <p className="text-gray-900 font-medium pl-6">{task.listName}</p>
                    </div>
                  )}

                  {/* Category */}
                  {task.category && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Folder size={18} className="text-gray-500" />
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Category</h3>
                      </div>
                      <p className="text-gray-900 font-medium pl-6">{task.category}</p>
                    </div>
                  )}

                  {/* Subcategory */}
                  {task.subcategory && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Folder size={18} className="text-gray-500" />
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Subcategory</h3>
                      </div>
                      <p className="text-gray-900 font-medium pl-6">{task.subcategory}</p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {task.tags && task.tags.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag size={18} className="text-gray-500" />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Tags</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-6">
                      {task.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-sm px-3 py-1 rounded-full bg-gray-100 text-gray-700 font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock size={18} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Metadata</h3>
                  </div>
                  <div className="pl-6 space-y-2 text-sm text-gray-600">
                    {task.createdAt && (
                      <p>
                        <span className="font-medium">Created:</span>{' '}
                        {new Date(task.createdAt).toLocaleString('en-GB', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })}
                      </p>
                    )}
                    {task.updatedAt && (
                      <p>
                        <span className="font-medium">Last Updated:</span>{' '}
                        {new Date(task.updatedAt).toLocaleString('en-GB', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })}
                      </p>
                    )}
                    {task.completedAt && (
                      <p>
                        <span className="font-medium">Completed:</span>{' '}
                        {new Date(task.completedAt).toLocaleString('en-GB', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
